import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  RESPONSE_CODE_200,
  RESPONSE_CODE_406,
} from "../Constants/CommonConstants";
import {
  API_INITIAL_STATE,
  BUILDER_TYPE_FULFILLED,
  BUILDER_TYPE_PENDING,
  BUILDER_TYPE_REJECTED,
  SLICE_MY_PROFILE,
  THUNK_GET_ASSOCIATED_LOCATIONS,
  THUNK_GET_DASHBOARD_DETAILS,
  THUNK_GET_MY_PROFILE,
  THUNK_POST_MESSAGE_CENTER_INBOX,
  THUNK_POST_NEW_PASSWORD_MY_PROFILE,
  THUNK_POST_PROVIDER_MESSAGE_CENTER,
  THUNK_PUT_ACCOUNT_NAME_SETTINGS,
  THUNK_PUT_NAME,
  THUNK_PUT_RESET_MY_PROFILE_PASSWORD,
} from "../Constants/SliceAndThunkConstants";
import { apiSliceBuilderService } from "../Services/CommonServices";
import {
  getAssociatedLocationsService,
  getDashboardDetailsService,
  getMyProfileService,
  postMessageCenterService,
  postNewPasswordMyProfileService,
  postProviderMessageCenterService,
  putAccountNameSettingsService,
  putNameService,
  putResetMyProfilePasswordService,
} from "../Services/MyProfileService";
import { saveVerificationMethodsData } from "./LoginSlice";
import { setFirstNameLastName } from "./AuthorizationSlice";

export const putResetMyProfilePassword = createAsyncThunk(
  `${SLICE_MY_PROFILE}/${THUNK_PUT_RESET_MY_PROFILE_PASSWORD}`,
  async (data, { rejectWithValue }) => {
    try {
      return await putResetMyProfilePasswordService(data);
    } catch (error) {
      return rejectWithValue(error?.response);
    }
  }
);

export const postNewPasswordMyProfile = createAsyncThunk(
  `${SLICE_MY_PROFILE}/${THUNK_POST_NEW_PASSWORD_MY_PROFILE}`,
  async ({ data, apiAction }, { rejectWithValue }) => {
    try {
      return await postNewPasswordMyProfileService(data, apiAction);
    } catch (error) {
      return rejectWithValue(error?.response);
    }
  }
);

export const getMyProfile = createAsyncThunk(
  `${SLICE_MY_PROFILE}/${THUNK_GET_MY_PROFILE}`,
  async (_, { dispatch }) => {
    const response = await getMyProfileService();
    if (response?.data?.message?.code === RESPONSE_CODE_200) {
      dispatch(saveVerificationMethodsData(response.data));
    }
    return response;
  }
);

export const putName = createAsyncThunk(
  `${SLICE_MY_PROFILE}/${THUNK_PUT_NAME}`,
  async (payload, { dispatch }) => {
    const response = await putNameService(payload);
    if (response?.data?.message?.code === RESPONSE_CODE_200) {
      dispatch(setFirstNameLastName(payload));
      dispatch(getMyProfile());
      dispatch(putAccountNameSettings(payload));
    }
    return response;
  }
);

export const putAccountNameSettings = createAsyncThunk(
  `${SLICE_MY_PROFILE}/${THUNK_PUT_ACCOUNT_NAME_SETTINGS}`,
  async (payload) => {
    const response = await putAccountNameSettingsService(payload);
    return response;
  }
);

export const getDashboardDetails = createAsyncThunk(
  `${SLICE_MY_PROFILE}/${THUNK_GET_DASHBOARD_DETAILS}`,
  async () => {
    const response = await getDashboardDetailsService();
    return response;
  }
);

export const postMessageCenterInbox = createAsyncThunk(
  `${SLICE_MY_PROFILE}/${THUNK_POST_MESSAGE_CENTER_INBOX}`,
  async (payload) => {
    const requestPayload = {
      archive: false,
      paginationParams: {
        pageNumber: payload?.pageNumber ? payload?.pageNumber - 1 : 0,
        recordsPerPage: 25,
      },
      dateRange: {},
      notificationType: {},
    };

    const response = await postMessageCenterService(requestPayload);
    return response;
  }
);

export const getAssociatedLocations = createAsyncThunk(
  `${SLICE_MY_PROFILE}/${THUNK_GET_ASSOCIATED_LOCATIONS}`,
  async (payload) => {
    const response = await getAssociatedLocationsService(payload);
    return response;
  }
);

export const postProviderMessageCenter = createAsyncThunk(
  `${SLICE_MY_PROFILE}/${THUNK_POST_PROVIDER_MESSAGE_CENTER}`,
  async () => {
    const payload = {
      fetchNotification: false,
      paginationDto: {
        pageNumber: 0,
        recordsPerPage: 25,
      },
    };
    const response = await postProviderMessageCenterService(payload);
    return response;
  }
);

const initialState = {
  removeVerificationMethodDescription: "",
  changePrefMethodDescription: "",
  editOrRegisterMethod: "",
  getMyProfileResponse: API_INITIAL_STATE,
  putNameResponse: API_INITIAL_STATE,
  putResetMyProfilePasswordResponse: API_INITIAL_STATE,
  postNewPasswordMyProfileResponse: API_INITIAL_STATE,
  getDashboardDetailsResponse: API_INITIAL_STATE,
  putAccountNameSettingsResponse: API_INITIAL_STATE,
  postMessageCenterInboxResponse: API_INITIAL_STATE,
  getAssociatedLocationsResponse: API_INITIAL_STATE,
  postProviderMessageCenterResponse: API_INITIAL_STATE,
  profileData: {
    userName: "",
    dob: "",
    email: "",
    telephoneNumber: "",
    firstName: "",
    lastName: "",
    middleName: "",
    preferredVerificationMethod: "",
    emailLanguage: "",
    addEditEmailLanguage: "",
  },
  associatedLocationsData: [],
  applicationStatus: "",
  messageCenter: [],
  unreadMessageCount: 0,
};

export const myProfileSlice = createSlice({
  name: SLICE_MY_PROFILE,
  initialState,
  reducers: {
    clearMyProfileApiResponse: (state, action) => {
      const responseName = `${action.payload}Response`;
      if (!initialState[responseName]) {
        return;
      }
      state[responseName] = initialState[responseName];
    },
    saveAddEditEmailLanguage: (state, action) => {
      state.profileData.addEditEmailLanguage = action.payload;
    },
    clearAddEditEmailLanguage: (state) => {
      state.profileData.addEditEmailLanguage = "";
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getMyProfile.pending, (state) => {
      state.getMyProfileResponse = apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(getMyProfile.rejected, (state, action) => {
      state.getMyProfileResponse = apiSliceBuilderService(
        BUILDER_TYPE_REJECTED,
        action
      );
      state.profileData = initialState.profileData;
    });
    builder.addCase(getMyProfile.fulfilled, (state, action) => {
      state.getMyProfileResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );

      const data = action.payload?.data?.data;
      state.profileData.dob = data?.dob;
      state.profileData.email = data?.email;
      state.profileData.firstName = data?.firstName;
      state.profileData.lastName = data?.lastName;
      state.profileData.middleName = data?.middleName;
      state.profileData.preferredVerificationMethod =
        data?.preferredVerificationMethod;
      state.profileData.telephoneNumber = data?.telephoneNumber;
      state.profileData.userName = data?.userName;
      state.profileData.emailLanguage = data?.prefEmailLanguage;
    });
    builder.addCase(putName.pending, (state) => {
      state.putNameResponse = apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(putName.fulfilled, (state, action) => {
      state.putNameResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
    });
    builder.addCase(putResetMyProfilePassword.pending, (state) => {
      state.putResetMyProfilePasswordResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(putResetMyProfilePassword.rejected, (state, action) => {
      if (action?.payload?.status === RESPONSE_CODE_406) {
        state.putResetMyProfilePasswordResponse = apiSliceBuilderService(
          BUILDER_TYPE_REJECTED,
          action
        );
      }
    });
    builder.addCase(putResetMyProfilePassword.fulfilled, (state, action) => {
      state.putResetMyProfilePasswordResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
    });
    builder.addCase(postNewPasswordMyProfile.pending, (state) => {
      state.postNewPasswordMyProfileResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(postNewPasswordMyProfile.rejected, (state, action) => {
      if (action?.payload?.status === RESPONSE_CODE_406) {
        state.postNewPasswordMyProfileResponse = apiSliceBuilderService(
          BUILDER_TYPE_REJECTED,
          action
        );
      }
    });
    builder.addCase(postNewPasswordMyProfile.fulfilled, (state, action) => {
      state.postNewPasswordMyProfileResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
    });
    builder.addCase(getDashboardDetails.pending, (state) => {
      state.getDashboardDetailsResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(getDashboardDetails.rejected, (state, action) => {
      state.getDashboardDetailsResponse = apiSliceBuilderService(
        BUILDER_TYPE_REJECTED,
        action
      );
      state.dashboardDetails = initialState.dashboardDetails;
    });
    builder.addCase(getDashboardDetails.fulfilled, (state, action) => {
      state.getDashboardDetailsResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );

      state.applicationStatus = action?.payload?.data?.data.appDisplayStatus;
    });
    builder.addCase(putAccountNameSettings.pending, (state, action) => {
      state.putAccountNameSettingsResponse = apiSliceBuilderService(
        BUILDER_TYPE_PENDING,
        action
      );
    });
    builder.addCase(putAccountNameSettings.rejected, (state, action) => {
      state.putAccountNameSettingsResponse = apiSliceBuilderService(
        BUILDER_TYPE_REJECTED,
        action
      );
    });
    builder.addCase(putAccountNameSettings.fulfilled, (state, action) => {
      state.putAccountNameSettingsResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
    });
    builder.addCase(postMessageCenterInbox.pending, (state) => {
      state.postMessageCenterInboxResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
      state.messageCenter = [];
    });

    builder.addCase(postMessageCenterInbox.fulfilled, (state, action) => {
      state.postMessageCenterInboxResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );

      state.messageCenter = action?.payload?.data?.data?.page?.content.filter(
        (data) => !data.archive
      );
    });
    builder.addCase(postMessageCenterInbox.rejected, (state) => {
      state.postMessageCenterInboxResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
      state.messageCenter = [];
    });
    builder.addCase(getAssociatedLocations.pending, (state) => {
      state.getAssociatedLocationsResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(getAssociatedLocations.fulfilled, (state, action) => {
      state.getAssociatedLocationsResponse = apiSliceBuilderService(
        BUILDER_TYPE_REJECTED,
        action
      );
      state.associatedLocationsData = action?.payload?.data?.data;
    });

    builder.addCase(postProviderMessageCenter.pending, (state) => {
      state.postProviderMessageCenterResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(postProviderMessageCenter.fulfilled, (state, action) => {
      state.postProviderMessageCenterResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
      state.unreadMessageCount =
        action?.payload?.data?.data?.page?.metadata?.totalRecords;
    });
  },
});

export const {
  clearMyProfileApiResponse,
  saveAddEditEmailLanguage,
  clearAddEditEmailLanguage,
} = myProfileSlice.actions;
export default myProfileSlice.reducer;
